import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Database,
  FileSearch,
  KeyRound,
  Link as LinkIcon,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  XCircle,
} from 'lucide-react';

type Environment = 'not-configured' | 'assurance' | 'production';

interface ChecklistItem {
  id: string;
  label: string;
  owner: string;
  done: boolean;
}

const gpProducts = [
  'Access Record: HTML',
  'Access Record: Structured',
  'Access Document',
  'Foundations',
];

const initialChecklist: ChecklistItem[] = [
  {
    id: 'use-case',
    label: 'Approved direct-care use case documented',
    owner: 'Registered manager',
    done: false,
  },
  {
    id: 'supplier-onboarding',
    label: 'NHS GP Connect supplier onboarding started',
    owner: 'System supplier',
    done: false,
  },
  {
    id: 'spine',
    label: 'Spine connectivity and SDS registration confirmed',
    owner: 'Technical lead',
    done: false,
  },
  {
    id: 'certificates',
    label: 'NHS-issued mutual TLS certificates available',
    owner: 'Technical lead',
    done: false,
  },
  {
    id: 'clinical-safety',
    label: 'Clinical safety case and hazard log prepared',
    owner: 'Clinical safety officer',
    done: false,
  },
  {
    id: 'ig',
    label: 'DPIA, DSPT and data-sharing controls reviewed',
    owner: 'Information governance',
    done: false,
  },
];

const auditRows = [
  {
    time: '2026-05-20 18:35',
    actor: 'Badan Catalin',
    event: 'GP Connect readiness module opened',
    outcome: 'Local audit only',
  },
  {
    time: '2026-05-20 18:36',
    actor: 'System',
    event: 'NHS connection status checked',
    outcome: 'Not connected',
  },
  {
    time: '2026-05-20 18:37',
    actor: 'System',
    event: 'Patient record exchange attempted',
    outcome: 'Blocked until NHS onboarding is complete',
  },
];

const accessSections = [
  'Allergies and adverse reactions',
  'Current medication summary',
  'Immunisations',
  'Problems and diagnoses',
  'Consultations and encounters',
  'Documents and letters',
];

export const GPConnect: React.FC = () => {
  const [environment, setEnvironment] = useState<Environment>('not-configured');
  const [odsCode, setOdsCode] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [certificateReference, setCertificateReference] = useState('');
  const [checklist, setChecklist] = useState(initialChecklist);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

  const completedCount = checklist.filter((item) => item.done).length;
  const readiness = Math.round((completedCount / checklist.length) * 100);
  const hasMinimumConfig = environment !== 'not-configured' && odsCode.trim() && endpoint.trim();
  const isReadyForAssurance = checklist.every((item) => item.done) && hasMinimumConfig && certificateReference.trim();

  const blockers = useMemo(() => {
    const missing = checklist.filter((item) => !item.done).map((item) => item.label);
    if (environment === 'not-configured') missing.unshift('NHS environment not selected');
    if (!odsCode.trim()) missing.unshift('Organisation ODS code missing');
    if (!endpoint.trim()) missing.unshift('GP Connect endpoint not configured');
    if (!certificateReference.trim()) missing.unshift('Mutual TLS certificate reference missing');
    return missing;
  }, [certificateReference, checklist, endpoint, environment, odsCode]);

  const runReadinessCheck = () => {
    setLastCheckedAt(new Date().toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }));
  };

  const toggleChecklistItem = (id: string) => {
    setChecklist((items) =>
      items.map((item) =>
        item.id === id ? { ...item, done: !item.done } : item
      )
    );
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <Stethoscope className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                NHS Integration
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">GP Connect</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                This module prepares Fuchsia Home Care for GP Connect assurance. Live GP record
                access remains blocked until NHS onboarding, approved direct-care use, Spine
                connectivity and mutual TLS certificates are in place.
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
            Status: Not connected to NHS
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Readiness</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{readiness}%</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Products</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{gpProducts.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Checklist</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">
            {completedCount}/{checklist.length}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Connection</p>
          <p className="mt-2 text-2xl font-bold text-slate-950">Blocked</p>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <KeyRound className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Connector Configuration</h2>
          </div>
          <div className="mt-5 space-y-4">
            <div>
              <label htmlFor="gp-connect-environment" className="block text-sm font-medium text-slate-800">Environment</label>
              <select
                id="gp-connect-environment"
                value={environment}
                onChange={(event) => setEnvironment(event.target.value as Environment)}
                className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              >
                <option value="not-configured">Not configured</option>
                <option value="assurance">NHS assurance/test</option>
                <option value="production">NHS production</option>
              </select>
            </div>
            <div>
              <label htmlFor="gp-connect-ods-code" className="block text-sm font-medium text-slate-800">Organisation ODS Code</label>
              <input
                id="gp-connect-ods-code"
                value={odsCode}
                onChange={(event) => setOdsCode(event.target.value)}
                placeholder="Enter ODS code after NHS confirmation"
                className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </div>
            <div>
              <label htmlFor="gp-connect-endpoint" className="block text-sm font-medium text-slate-800">GP Connect Endpoint</label>
              <input
                id="gp-connect-endpoint"
                value={endpoint}
                onChange={(event) => setEndpoint(event.target.value)}
                placeholder="NHS-approved endpoint URL"
                className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </div>
            <div>
              <label htmlFor="gp-connect-certificate" className="block text-sm font-medium text-slate-800">Certificate Reference</label>
              <input
                id="gp-connect-certificate"
                value={certificateReference}
                onChange={(event) => setCertificateReference(event.target.value)}
                placeholder="mTLS certificate reference"
                className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </div>
            <button
              type="button"
              onClick={runReadinessCheck}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
            >
              <RefreshCw className="h-4 w-4" />
              Run readiness check
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">NHS Assurance Checklist</h2>
          </div>
          <div className="mt-5 space-y-3">
            {checklist.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleChecklistItem(item.id)}
                className="flex w-full items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-[#6c3a80]/40 hover:bg-white"
              >
                {item.done ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                ) : (
                  <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-400" />
                )}
                <span>
                  <span className="block text-sm font-semibold text-slate-950">{item.label}</span>
                  <span className="mt-1 block text-xs text-slate-500">Owner: {item.owner}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Readiness Result</h2>
          </div>
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              {isReadyForAssurance ? (
                <CheckCircle2 className="mt-1 h-5 w-5 text-emerald-600" />
              ) : (
                <AlertTriangle className="mt-1 h-5 w-5 text-amber-600" />
              )}
              <div>
                <p className="font-semibold text-slate-950">
                  {isReadyForAssurance
                    ? 'Ready to submit for NHS assurance review'
                    : 'Not ready for live GP Connect access'}
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {lastCheckedAt
                    ? `Last checked: ${lastCheckedAt}`
                    : 'Run the readiness check after updating the configuration.'}
                </p>
              </div>
            </div>
          </div>
          {!isReadyForAssurance && (
            <div className="mt-4 space-y-2">
              {blockers.slice(0, 6).map((blocker) => (
                <div key={blocker} className="flex gap-2 text-sm leading-6 text-slate-600">
                  <AlertTriangle className="mt-1 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <span>{blocker}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <Database className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Planned Record Sections</h2>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {accessSections.map((section) => (
              <div key={section} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <FileSearch className="h-4 w-4 text-[#6c3a80]" />
                  {section}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <LockKeyhole className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Audit and Safety Controls</h2>
          </div>
          <div className="mt-5 overflow-hidden rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {auditRows.map((row) => (
                  <tr key={`${row.time}-${row.event}`}>
                    <td className="px-4 py-3 text-slate-600">{row.time}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.actor}</td>
                    <td className="px-4 py-3 text-slate-600">{row.event}</td>
                    <td className="px-4 py-3 text-slate-600">{row.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <LinkIcon className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Official NHS References</h2>
          </div>
          <div className="mt-5 space-y-3 text-sm leading-6">
            <a
              href="https://digital.nhs.uk/services/gp-connect"
              target="_blank"
              rel="noreferrer"
              className="block rounded-md border border-slate-200 p-3 font-medium text-[#6c3a80] hover:bg-slate-50"
            >
              NHS England GP Connect service overview
            </a>
            <a
              href="https://digital.nhs.uk/services/gp-connect/develop-gp-connect-services/development"
              target="_blank"
              rel="noreferrer"
              className="block rounded-md border border-slate-200 p-3 font-medium text-[#6c3a80] hover:bg-slate-50"
            >
              GP Connect development guidance
            </a>
            <a
              href="https://standards.nhs.uk/published-standards/gp-connect-access-document-fhir-api"
              target="_blank"
              rel="noreferrer"
              className="block rounded-md border border-slate-200 p-3 font-medium text-[#6c3a80] hover:bg-slate-50"
            >
              GP Connect Access Document FHIR standard
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
